import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Space, 
  Statistic, 
  Tag, 
  Alert, 
  Spin, 
  Empty,
  List,
  Divider
} from 'antd';
import {
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { 
  getAssignments, 
  getOngoingAssignments, 
  getCompliancePending,
  getAgencyOrg 
} from '../../api';
import './Dashboard.css';

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    assignments: [],
    ongoingAssignments: [],
    compliancePending: [],
    agencyInfo: null
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [
        assignmentsResponse,
        ongoingResponse,
        complianceResponse,
        agencyResponse
      ] = await Promise.allSettled([
        getAssignments(),
        getOngoingAssignments(),
        getCompliancePending(),
        getAgencyOrg()
      ]);

      const assignments = assignmentsResponse.status === 'fulfilled' ? assignmentsResponse.value.data : [];
      const ongoing = ongoingResponse.status === 'fulfilled' ? ongoingResponse.value.data : [];
      const compliance = complianceResponse.status === 'fulfilled' ? complianceResponse.value.data : [];
      const agency = agencyResponse.status === 'fulfilled' ? agencyResponse.value.data : null;

      setDashboardData({
        assignments,
        ongoingAssignments: ongoing,
        compliancePending: compliance,
        agencyInfo: agency
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'ongoing': return 'processing';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getRecentAssignments = () => {
    return dashboardData.assignments
      .sort((a, b) => new Date(b.createdAt || b.updatedAt) - new Date(a.createdAt || a.updatedAt))
      .slice(0, 5);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '0', background: '#fafafa' }}>
      {/* Header */}
      <div style={{ 
        background: '#ffffff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #f0f0f0'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#262626' }}>
              Agency Dashboard
            </Title>
            <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
              {dashboardData.agencyInfo?.name || 'Overview of your agency operations'}
            </Text>
          </Col>
          <Col>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchDashboardData}
              style={{ borderRadius: '6px' }}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Total Assignments"
              value={dashboardData.assignments.length}
              prefix={<FileTextOutlined style={{ color: '#595959' }} />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Ongoing Assignments"
              value={dashboardData.ongoingAssignments.length}
              prefix={<TeamOutlined style={{ color: '#595959' }} />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Compliance Pending"
              value={dashboardData.compliancePending.length}
              prefix={<ExclamationCircleOutlined style={{ color: '#595959' }} />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Completed"
              value={dashboardData.assignments.filter(a => a.status === 'completed').length}
              prefix={<CheckCircleOutlined style={{ color: '#595959' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Recent Assignments */}
        <Col xs={24} lg={16}>
          <Card 
            title="Recent Assignments"
            extra={
              <Button 
                type="link" 
                icon={<EyeOutlined />}
                onClick={() => navigate('/agency/assignments')}
                style={{ padding: 0 }}
              >
                View All
              </Button>
            }
            style={{ borderRadius: '8px' }}
          >
            {getRecentAssignments().length > 0 ? (
              <List
                dataSource={getRecentAssignments()}
                renderItem={assignment => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{assignment.title || assignment.position || 'Assignment'}</Text>
                          <Tag color={getStatusColor(assignment.status)}>
                            {assignment.status || 'pending'}
                          </Tag>
                        </Space>
                      }
                      description={
                                                 <Space split={<span style={{ color: '#d9d9d9' }}>•</span>}>
                           {assignment.department && (
                             <Text type="secondary">
                               {typeof assignment.department === 'string' ? assignment.department : String(assignment.department)}
                             </Text>
                           )}
                           {assignment.location && (
                             <Text type="secondary">
                               {typeof assignment.location === 'string' ? assignment.location : String(assignment.location)}
                             </Text>
                           )}
                           {assignment.candidateName && (
                             <Text type="secondary">
                               {typeof assignment.candidateName === 'string' ? assignment.candidateName : String(assignment.candidateName)}
                             </Text>
                           )}
                           {(assignment.createdAt || assignment.updatedAt) && (
                             <Text type="secondary">
                               {new Date(assignment.createdAt || assignment.updatedAt).toLocaleDateString()}
                             </Text>
                           )}
                         </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No assignments found" />
            )}
          </Card>
        </Col>

        {/* Alerts & Actions */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
     
            {/* Quick Actions */}
            <Card 
              title="Quick Actions"
              style={{ borderRadius: '8px' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  block 
                  onClick={() => navigate('/agency/assignments')}
                  style={{ textAlign: 'left', borderRadius: '6px' }}
                >
                  <FileTextOutlined /> View All Assignments
                </Button>
                
                <Button 
                  block 
                  onClick={() => navigate('/agency/ongoing-management')}
                  style={{ textAlign: 'left', borderRadius: '6px' }}
                >
                  <TeamOutlined /> Manage Ongoing
                </Button>
                
                {dashboardData.compliancePending.length > 0 && (
                  <Button 
                    block 
                    onClick={() => navigate('/agency/compliance-pending')}
                    style={{ textAlign: 'left', borderRadius: '6px' }}
                  >
                    <ExclamationCircleOutlined /> Review Compliance
                  </Button>
                )}
              </Space>
            </Card>

            {/* Agency Info */}
            {dashboardData.agencyInfo && (
              <Card 
                title="Agency Information"
                style={{ borderRadius: '8px' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {dashboardData.agencyInfo.name && (
                    <div>
                      <Text type="secondary">Name</Text>
                      <br />
                      <Text strong>{dashboardData.agencyInfo.name}</Text>
                    </div>
                  )}
                  
                  {dashboardData.agencyInfo.email && (
                    <div>
                      <Text type="secondary">Email</Text>
                      <br />
                      <Text>{dashboardData.agencyInfo.email}</Text>
                    </div>
                  )}
                  
                  {dashboardData.agencyInfo.phone && (
                    <div>
                      <Text type="secondary">Phone</Text>
                      <br />
                      <Text>{dashboardData.agencyInfo.phone}</Text>
                    </div>
                  )}
                  
                  <Divider style={{ margin: '12px 0' }} />
                  
                  <Button 
                    type="link" 
                    onClick={() => navigate('/agency/setting')}
                    style={{ padding: 0 }}
                  >
                    Update Agency Details
                  </Button>
                </Space>
              </Card>
            )}
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
